import {
  Calendar,
  CalendarAutoScheduleFrom,
  CalendarEvent,
  CalendarScheduleFrom,
  ChatSessionType,
  defaultEventCategorySet,
  EnergyBoostPreferenceDefaultActivities,
  EventCategorySet,
  MetricActivity,
  MetricMealDetail,
  NapItem,
  ScheduledAction,
  ScheduledActionOptions,
  TaskItem,
  TaskLabel,
} from '@demind-inc/core';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { ActivityCalendarEvent, MetricActivityType, SuggestedEventType, UnifiedChatRichContext } from '../data-access';
import { round, upperFirst } from 'lodash';
import { CALENDAR_STEP_MIN } from '../components';
import { transformTaskLabelsToEventCategories } from './eventCategory.helpers';

export { resolveEventCategoryFromCategoryId, transformTaskLabelsToEventCategories } from './eventCategory.helpers';

export const ACTIVITY_EVENT_PREFIX = 'activity';
export const ENERGY_BOOST_EVENT_PREFIX = 'energy_boost';
export const AI_SCHEDULER_EVENT_PREFIX = 'ai_scheduler';
const exerciseCategory = defaultEventCategorySet.find(({ id }) => id === 'default_exercise');
const mealCategory = defaultEventCategorySet.find(({ id }) => id === 'default_meal');

export const convertTaskToEvent = (task: TaskItem): CalendarEvent => {
  const categories = transformTaskLabelsToEventCategories(task.labels);

  return {
    eventId: 'creating',
    summary: task.name,
    description: `${task.desc} [Created from Lifestack (https://lifestack.ai)].`,
    color: task.labels?.[0]?.color,
    scheduleFrom: 'todo',
    autoScheduleFrom: {
      from: 'todo',
      id: task.taskId,
      predicted: false,
      editedManually: true,
    },
    start: {
      date: '',
      timeZone: dayjs.tz.guess(),
    },
    end: {
      date: '',
      timeZone: dayjs.tz.guess(),
    },
    taskFrom: {
      taskId: task.taskId,
      boardId: task.boardId!,
      from: task.appFrom,
    },
    categories,
    updatedDate: dayjs().toISOString(),
    createdDate: dayjs().toISOString(),
  };
};

/**
 * Translates the scheduled action options to a temporal calendar event.
 * @param actionOptions - The scheduled action options.
 * @returns The translated calendar event.
 */
export const translateScheduledActionOptionsToTemporalCalendarEvent = (
  actionOptions: ScheduledActionOptions,
  defaultActivities?: EnergyBoostPreferenceDefaultActivities
): CalendarEvent => {
  const defaultActivity =
    actionOptions.options.find(
      (opt) =>
        !!opt.taskId && Object.values(defaultActivities || {}).some((act) => act === opt.taskId)
    ) ?? actionOptions.options[0];
  return {
    eventId: `${ENERGY_BOOST_EVENT_PREFIX}_${actionOptions.startDate}`,
    description: 'Energy-boosting event, created from Lifestack (https://lifestack.ai).',
    summary: defaultActivity.name,
    start: {
      date: actionOptions.startDate,
      timeZone: dayjs.tz.guess(),
    },
    end: {
      date: actionOptions.endDate,
      timeZone: dayjs.tz.guess(),
    },
    updatedDate: dayjs().toISOString(),
    createdDate: dayjs().toISOString(),
    scheduleFrom: 'energyBoost',
  };
};

/**
 * Translates a ScheduledAction object to a CalendarEvent object.
 * @param action - The ScheduledAction object to be translated.
 * @returns The translated CalendarEvent object.
 */
export const translateScheduledActionToCalendarEvent = (action: ScheduledAction): CalendarEvent => {
  return {
    eventId: (uuidv4() as string).replace(/-/g, ''),
    summary: action.name,
    description: action.description!,
    start: {
      date: action.startDate,
      timeZone: dayjs.tz.guess(),
    },
    end: {
      date: action.endDate,
      timeZone: dayjs.tz.guess(),
    },
    status: 'confirmed',
    updatedDate: dayjs().toISOString(),
    createdDate: dayjs().toISOString(),
    eventMetrics: {
      energy: 'high',
    },
    recommendedTaskId: action.taskId,
    scheduleFrom: 'energyBoost',
  };
};

/**
 * Translates a MetricActivity object to a CalendarEvent object.
 *
 * @param activity - The MetricActivity object to be translated.
 * @returns A CalendarEvent object containing the translated activity details.
 */
export const translateActivityToTemporalCalendarEvent = (
  activity: MetricActivity | MetricMealDetail,
  activityType: MetricActivityType
): ActivityCalendarEvent => {
  const category = activityType === 'exercise' ? exerciseCategory : mealCategory;

  const statsLine =
    activityType === 'exercise'
      ? [
          (activity as MetricActivity).steps != null &&
            `${(activity as MetricActivity).steps!.toLocaleString()} steps`,
          (activity as MetricActivity).calories != null &&
            `${round((activity as MetricActivity).calories!)} kcal`,
          (activity as MetricActivity).distanceMeters != null &&
            `${round((activity as MetricActivity).distanceMeters! / 1000, 2)} km`,
        ]
          .filter(Boolean)
          .join(' · ')
      : '';

  const description = statsLine
    ? `${statsLine}\nCreated from Lifestack (https://lifestack.ai)`
    : `${upperFirst(activityType)} event, created from Lifestack (https://lifestack.ai).`;

  return {
    eventId: `${ACTIVITY_EVENT_PREFIX}_${activity.activityId}`,
    summary: activity.name,
    description,
    start: {
      date: dayjs(activity.startTime).toISOString(),
      timeZone: dayjs.tz.guess(),
    },
    end: {
      date: dayjs(activity.endTime).toISOString(),
      timeZone: dayjs.tz.guess(),
    },
    updatedDate: dayjs().toISOString(),
    createdDate: dayjs().toISOString(),
    eventMetrics: {
      energy: 'high',
    },
    categories: category ? [category] : [],
    activityFrom: {
      activityId: activity.activityId,
    },
    scheduleFrom: activityType === 'exercise' ? 'workout' : 'activity',
    source: activity.source!,
    activityType,
    steps: (activity as MetricActivity).steps,
    calories: (activity as MetricActivity).calories,
    distanceMeters: (activity as MetricActivity).distanceMeters,
  };
};

interface WorkoutSummaryInput {
  name: string;
  startTime: string;
  endTime: string;
  steps?: number;
  calories?: number;
  distanceMeters?: number;
  source?: string;
}

type ActivityEventWorkoutInput = Pick<
  ActivityCalendarEvent,
  'summary' | 'start' | 'end' | 'steps' | 'calories' | 'distanceMeters' | 'source'
>;

export const buildWorkoutSummaryRichContext = (
  activity: WorkoutSummaryInput
): UnifiedChatRichContext => {
  const durationMin = dayjs(activity.endTime).diff(dayjs(activity.startTime), 'minute');

  return {
    content: [
      {
        type: 'workout_summary',
        payload: {
          workout_type: activity.name,
          duration_min: durationMin > 0 ? durationMin : undefined,
          ...(activity.steps != null && activity.steps > 0 ? { steps: activity.steps } : {}),
          ...(activity.calories != null ? { calories: activity.calories } : {}),
          ...(activity.distanceMeters != null
            ? { distance_meters: activity.distanceMeters }
            : {}),
          ...(activity.source ? { source: activity.source } : {}),
        },
      },
    ],
  } as unknown as UnifiedChatRichContext;
};

export const buildWorkoutSummaryRichContextFromActivityEvent = (
  event: ActivityEventWorkoutInput,
  timeOverrides?: { startTime?: string; endTime?: string }
): UnifiedChatRichContext =>
  buildWorkoutSummaryRichContext({
    name: event.summary ?? '',
    startTime: timeOverrides?.startTime ?? event.start.date,
    endTime: timeOverrides?.endTime ?? event.end.date,
    steps: event.steps,
    calories: event.calories,
    distanceMeters: event.distanceMeters,
    source: event.source,
  });

export const translateNapToTemporalCalendarEvent = ({
  metricNapId,
  detail,
}: NapItem): ActivityCalendarEvent | null => {
  if (!detail.sleepTime || !detail.awakeTime) {
    return null;
  }

  return {
    eventId: `${ACTIVITY_EVENT_PREFIX}_${metricNapId}`,
    summary: 'Nap',
    description: detail.source ? `Source: ${detail.source}` : undefined,
    start: {
      date: dayjs(detail.sleepTime).toISOString(),
      timeZone: dayjs.tz.guess(),
    },
    end: {
      date: dayjs(detail.awakeTime).toISOString(),
      timeZone: dayjs.tz.guess(),
    },
    updatedDate: dayjs().toISOString(),
    createdDate: dayjs().toISOString(),
    eventMetrics: {
      energy: 'high',
    },
    activityFrom: {
      activityId: metricNapId,
    },
    scheduleFrom: 'activity',
    source: detail.source,
    activityType: 'nap',
    napEfficiency: detail.efficiency,
    napStages: detail.stages as Record<string, number> | undefined,
    napTemperatureDelta: detail.temperatureDelta,
  };
};

interface NapSummaryInput {
  startTime: string;
  endTime: string;
  efficiency?: number;
  stages?: Record<string, number>;
  temperatureDelta?: number;
}

type ActivityEventNapInput = Pick<
  ActivityCalendarEvent,
  'start' | 'end' | 'napEfficiency' | 'napStages' | 'napTemperatureDelta'
>;

export const buildNapSummaryRichContext = (nap: NapSummaryInput): UnifiedChatRichContext => {
  const durationMin = dayjs(nap.endTime).diff(dayjs(nap.startTime), 'minute');

  const convertedStages = nap.stages
    ? Object.entries(nap.stages).reduce(
        (acc, [key, value]) => {
          acc[key] = { duration_min: value };
          return acc;
        },
        {} as Record<string, { duration_min: number }>
      )
    : undefined;

  return {
    content: [
      {
        type: 'sleep_summary',
        payload: {
          duration_h: durationMin > 0 ? round(durationMin / 60, 1) : undefined,
          sleep_time: nap.startTime,
          awake_time: nap.endTime,
          ...(nap.efficiency != null ? { efficiency: nap.efficiency } : {}),
          ...(convertedStages ? { stages: convertedStages } : {}),
          ...(nap.temperatureDelta != null ? { temperature_delta: nap.temperatureDelta } : {}),
        },
      },
    ],
  } as unknown as UnifiedChatRichContext;
};

export const buildNapSummaryRichContextFromActivityEvent = (
  event: ActivityEventNapInput,
  timeOverrides?: { startTime?: string; endTime?: string }
): UnifiedChatRichContext =>
  buildNapSummaryRichContext({
    startTime: timeOverrides?.startTime ?? event.start.date,
    endTime: timeOverrides?.endTime ?? event.end.date,
    efficiency: event.napEfficiency,
    stages: event.napStages,
    temperatureDelta: event.napTemperatureDelta,
  });

export const getEventDateYYYYMMDD = (event: Partial<CalendarEvent>) => {
  if (!event.start?.date) {
    return;
  }
  return dayjs(event.start.date).format('YYYY-MM-DD');
};

/**
 * Checks if the event is an AI-suggested event.
 * @param autoScheduleFrom - The auto schedule from.
 * @returns True if the event is an AI-suggested event, false otherwise.
 */
export const isAISuggestedEvent = (autoScheduleFrom?: CalendarAutoScheduleFrom) => {
  return (['sleep', 'meal', 'todo', 'routine', 'workout'] as CalendarScheduleFrom[]).includes(
    autoScheduleFrom?.from!
  );
};

export const transformCalendarEventFromToSessionType = (
  from?: CalendarScheduleFrom
): SuggestedEventType | undefined => {
  if (!from) {
    return;
  }
  switch (from) {
    case 'sleep':
      return 'sleep';
    case 'meal':
      return 'meal';
    case 'aiScheduler':
      return 'scheduler';
    case 'todo':
      return 'task';
    case 'routine':
      return 'routine';
    case 'workout':
      return 'workout';
    default:
      return;
  }
};

/**
 * Scrolls to a specific time in the calendar.
 * @param targetTime - The target time to scroll to.
 */
export const scrollToCalendarTime = (targetTime: { date: string; time: string }) => {
  const calendarContainer = document.querySelector('.rbc-time-content');
  if (!calendarContainer) return;

  const targetDate = dayjs(`${targetTime.date} ${targetTime.time}`);
  const hours = targetDate.hour();
  const minutes = targetDate.minute();

  const slotHeight = 17.5;
  const step = CALENDAR_STEP_MIN;
  const slotsPerHour = 60 / step;

  const totalSlots = hours * slotsPerHour + Math.floor(minutes / step);
  const yOffset = totalSlots * slotHeight;

  calendarContainer.scrollTo({
    top: yOffset,
    behavior: 'smooth',
  });
};

/**
 * Checks if the event is an Apple Calendar event.
 * @param calendarId - The calendar id to check.
 * @param findCalendarItem - The function to find the calendar item.
 * @returns True if the event is an Apple Calendar event, false otherwise.
 */
export const isAppleEvent = (
  calendarId: string,
  findCalendarItem: (calendarId: string) => Calendar | undefined
) => {
  return calendarId && findCalendarItem(calendarId)?.calendarType === 'apple';
};

export const isTemporalEventId = (eventId?: string) => {
  return eventId?.startsWith('temporal');
};
